import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import Table from 'react-bootstrap/Table';
import queryString from 'query-string';
import { CurrencyPairsTableHeadRow } from './components/CurrencyPairsTableHeadRow';
import { CurrencyPairsTableRowContent } from './components/CurrencyPairsTableRowContent';
import { DIRECTION } from 'components/constants';
import EmptyTableContentPlug from 'components/EmptyTableContentPlug';
import i18nContext from 'components/i18n-context';
import Loader from 'components/Loader';
import Pagination from 'components/Pagination/Pagination';
import PopUpCreateNewCurrencyPairScheme from 'components/PopUpScheme/PopUpCreateNewCurrencyPairScheme';
import { useApplicationContext } from 'contexts/ApplicationContext';
import Button from 'uikit/Button/Button';
import { CheckBox } from 'uikit/CheckBox/CheckBox';
import { InputDropDownSearch } from 'uikit/InputDropDown/InputDropDownSearch';
import { PopUp } from 'uikit/PopUp/PopUp';
import './CurrencyPairsTable.scss';

const popUpInitialData = {
  isShowPopUp: false,
  isUpdate: false,
  currencyPairData: null,
  onCancel: () => {},
  onConfirm: () => {}
};

const CurrencyPairsTable = ({ markupsStore }) => {
  const i18n = useContext(i18nContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { constants } = useApplicationContext();
  const [popUpData, setPopUpData] = useState(popUpInitialData);

  const currencyOptions = constants?.currencies?.map((currency) => {
    return {
      key: currency,
      value: currency
    };
  });

  const updateFiltersInUrl = (filters, pagination) => {
    const queryParams = {
      page: pagination.page,
      size: pagination.size,
      sort_direction: pagination.direction
    };

    if (filters.from) {
      queryParams.from = filters.from;
    }
    if (filters.to) {
      queryParams.to = filters.to;
    }

    if (pagination.sortBy) {
      queryParams.sort_column = pagination.sortBy;
    }

    const searchParams = queryString.stringify(queryParams);
    navigate({ search: `?${searchParams}` }, { replace: true });
  };

  const loadFiltersFromUrl = () => {
    const params = queryString.parse(location.search);
    markupsStore.setFiltersFromUrl(params);
  };

  useEffect(() => {
    loadFiltersFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!markupsStore.isInitialized) {
      markupsStore.getCurrencyPairsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markupsStore.isInitialized]);

  useEffect(() => {
    if (markupsStore.isInitialized) {
      markupsStore.resetMarkupsStore();
      loadFiltersFromUrl();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    updateFiltersInUrl(markupsStore.markupsListFilters, markupsStore.pagination);
    if (markupsStore.isInitialized) {
      markupsStore.getCurrencyPairsList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    markupsStore.markupsListFilters.from,
    markupsStore.markupsListFilters.to,
    markupsStore.markupsListFilters.activeOnly,
    markupsStore.markupsListFilters.manualOnly,
    markupsStore.pagination.sortBy,
    markupsStore.pagination.direction,
    markupsStore.pagination.size,
    markupsStore.pagination.page
  ]);

  useEffect(() => {
    if (markupsStore.isNewCurrencyPairSuccessfullyCreated) {
      markupsStore.getCurrencyPairsList();
      markupsStore.setIsNewCurrencyPairSuccessfullyCreated(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markupsStore.isNewCurrencyPairSuccessfullyCreated]);

  const handleSetCurrentPage = (pageNumber) => {
    markupsStore.setMarkupGroupsPage(pageNumber);
  };

  const handleSetPageSize = (size) => {
    markupsStore.setMarkupGroupsPageSize(size);
  };

  const handleOnSortTable = (newSortBy) => {
    markupsStore.setSortData({
      sortBy: newSortBy,
      direction:
        markupsStore.pagination.sortBy !== newSortBy
          ? DIRECTION.ASC
          : markupsStore.pagination.direction === DIRECTION.ASC
            ? DIRECTION.DESC
            : DIRECTION.ASC
    });
  };

  const handleChangeCurrencyPairStatus = async (data, status) => {
    await markupsStore.updateCurrencyPair({ ...data, active: status });
  };

  const handleChangeCurrencyPairManual = async (data, manual) => {
    await markupsStore.updateCurrencyPair({ ...data, manual });
  };

  const handleUpdateCurrencyPair = async (data) => {
    await markupsStore.updateCurrencyPair(data);
  };

  const handleOpenCreateCurrencyPairPopUp = async () => {
    setPopUpData({
      isShowPopUp: true,
      isUpdate: false,
      currencyPairData: null,
      onConfirm: async (data) => {
        await markupsStore.createCurrencyPair(data);
        resetPopUpData();
      },
      onCancel: resetPopUpData
    });
  };

  const resetPopUpData = () => {
    setPopUpData(popUpInitialData);
  };

  const handleOpenUpdateCurrencyPairPopUp = async (currencyPairData) => {
    setPopUpData({
      isShowPopUp: true,
      isUpdate: true,
      currencyPairData: currencyPairData,
      onConfirm: async (data) => {
        await handleUpdateCurrencyPair(data);
        resetPopUpData();
      },
      onCancel: resetPopUpData
    });
  };

  if (!markupsStore.isInitialized) {
    return <Loader className={'application-loader'} />;
  }

  return (
    <div className={'currency-pairs-wrapper'}>
      <div className={'currency-pairs-actions-wrapper'}>
        <div className={'currency-pairs-filters-container'}>
          <InputDropDownSearch
            className={'currency-pairs-filter-wrapper'}
            name={'from'}
            label={i18n.getMessage('currencyPairs.filter.from.label')}
            value={markupsStore.markupsListFilters.from}
            options={currencyOptions}
            onChange={markupsStore.setMarkupsListFilter}
            isDisabled={markupsStore.isLoading}
            isClearValueAfterClickOutside={true}
          />
          <InputDropDownSearch
            className={'currency-pairs-filter-wrapper'}
            name={'to'}
            label={i18n.getMessage('currencyPairs.filter.to.label')}
            value={markupsStore.markupsListFilters.to}
            options={currencyOptions}
            onChange={markupsStore.setMarkupsListFilter}
            isDisabled={markupsStore.isLoading}
            isClearValueAfterClickOutside={true}
          />
          <CheckBox
            className={'currency-pairs-filter-checkbox'}
            name={'activeOnly'}
            isActive={markupsStore.markupsListFilters.activeOnly}
            isReverseView={false}
            showCheckBox={true}
            onChange={() =>
              markupsStore.setMarkupsListFilter('activeOnly', !markupsStore.markupsListFilters.activeOnly)
            }
            value={i18n.getMessage('currencyPairs.filter.activeOnly.label')}
            disabled={markupsStore.isLoading}
          />
          <CheckBox
            className={'currency-pairs-filter-checkbox'}
            name={'manualOnly'}
            isActive={markupsStore.markupsListFilters.manualOnly}
            isReverseView={false}
            showCheckBox={true}
            onChange={() =>
              markupsStore.setMarkupsListFilter('manualOnly', !markupsStore.markupsListFilters.manualOnly)
            }
            value={i18n.getMessage('currencyPairs.filter.manualOnly.label')}
            disabled={markupsStore.isLoading}
          />
        </div>

        <Button
          className={'currency-pairs-button'}
          size={'medium'}
          type={'primary'}
          onClick={handleOpenCreateCurrencyPairPopUp}
          isDisabled={!markupsStore.isInitialized}
        >
          {i18n.getMessage('currencyPairs.button.createNewCurrencyPair')}
        </Button>
      </div>

      {!markupsStore.currencyPairList || markupsStore.currencyPairList?.length === 0 ? (
        <EmptyTableContentPlug
          headerText={i18n.getMessage('currencyPairs.table.emptyPlug.header')}
          descriptionText={i18n.getMessage('currencyPairs.table.emptyPlug.description')}
        />
      ) : (
        <>
          <Pagination
            isShowPaginationSize={true}
            paginationSize={markupsStore.pagination.size}
            handleChangePaginationSize={handleSetPageSize}
            isLoading={markupsStore.isLoading}
            currentPage={markupsStore.pagination.page}
            numberOfPages={markupsStore.pagination.totalPages}
            setCurrentPage={handleSetCurrentPage}
          />

          <Table responsive>
            <thead>
              <CurrencyPairsTableHeadRow
                handleOnSortTable={handleOnSortTable}
                sortBy={markupsStore.pagination.sortBy}
                direction={markupsStore.pagination.direction}
              />
            </thead>
            <tbody>
              {markupsStore.currencyPairList?.map((currencyPairData, index) => (
                <CurrencyPairsTableRowContent
                  key={index}
                  type={'list'}
                  isLoading={markupsStore.isLoading}
                  handleChangeCurrencyPairStatus={handleChangeCurrencyPairStatus}
                  handleChangeCurrencyPairManual={handleChangeCurrencyPairManual}
                  currencyPairData={currencyPairData}
                  handleOpenUpdateCurrencyPairPopUp={handleOpenUpdateCurrencyPairPopUp}
                />
              ))}
            </tbody>
          </Table>

          <Pagination
            isShowPaginationSize={true}
            paginationSize={markupsStore.pagination.size}
            handleChangePaginationSize={handleSetPageSize}
            isLoading={markupsStore.isLoading}
            currentPage={markupsStore.pagination.page}
            numberOfPages={markupsStore.pagination.totalPages}
            setCurrentPage={handleSetCurrentPage}
          />
        </>
      )}

      <PopUp alignOnCenter show={popUpData.isShowPopUp}>
        <PopUpCreateNewCurrencyPairScheme
          isLoading={markupsStore.isLoading}
          handleClose={popUpData.onCancel}
          handleCreateNewCurrencyPair={popUpData.onConfirm}
          isUpdate={popUpData.isUpdate}
          currencyPairData={popUpData.currencyPairData}
        />
      </PopUp>
    </div>
  );
};

CurrencyPairsTable.propTypes = {
  markupsStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  markupsStore: stores.markupsStore
}))(observer(CurrencyPairsTable));
